
import { computed, defineComponent, ref } from 'vue';
import VRuntimeTemplate from 'vue3-runtime-template';

import { DataType } from '@/products/api/configuration/application/DataType';
import { ComponentName } from '@/products/api/configuration/components/ComponentName';
import { attrValue, attrValues, additionalInfo } from '@/common/helpers/productAttributes';
import { setupComponent } from '@/products/composables/setupComponent';
import { ConfiguratorItem, ProductItem } from '@/common/services/swagger/index.defs';
import {
  ConfiguratorCommand,
  DetailsCommand,
  PreviewCommand,
} from '@/products/api/runtime/CommandExecutor';
import { IConfiguratorOptions } from '@/products/api/configuration/components/IConfiguratorOptions';
import { IItemsGridOptions } from '@/products/api/configuration/components/IItemsGridOptions';

import InjectStyles from '@/common/components/InjectStyles.vue';
import Image from '@/common/components/Image.vue';
import PisReplacements from '@/common/components/PisReplacements.vue';
import Tooltip from '@/common/components/Tooltip.vue';
import Icon from '@/common/components/Icon.vue';
import Card from '@/common/components/Card.vue';
import Clickable from '@/common/components/Clickable.vue';

export default defineComponent({
  name: ComponentName.searchGrid,

  components: {
    InjectStyles,
    Image,
    Clickable,
    PisReplacements,
    Tooltip,
    Icon,
    Card,
    VRuntimeTemplate,
  },

  props: {
    instanceId: String,
  },

  setup(props) {
    const root = ref<HTMLElement>();

    const {
      componentName,
      isWebComponent,
      isReady,
      instance,
      store,
      routeData,
      t,
      widerThan,
      emit,
    } = setupComponent(root, props.instanceId, [DataType.Products]);

    const componentOptions = computed(
      () => store.value?.options.components?.itemsGrid ?? ({} as IItemsGridOptions),
    );

    const configuratorOptions = computed(
      () => store.value?.options.components?.configurators as IConfiguratorOptions,
    );

    const currentCIDName = computed(() => {
      const cid = instance.value?.router.routeData?.cid;
      if (cid) {
        const breadcrumbs = instance.value?.store?.data.breadcrumbs?.items;
        if (breadcrumbs) {
          return breadcrumbs[breadcrumbs.length - 1]?.name;
        }
      }
      return undefined;
    });

    const hideIfInitial = computed(() => {
      const history = instance.value?.router.history;
      return store.value?.options.hideInitialSearchResults && history && history.length <= 2;
    });

    const details = async (product: ProductItem) => {
      if (product?.inactive) return;

      if (routeData.value && product) {
        try {
          await instance.value?.execute(new DetailsCommand(product));
        } catch (error) {
          // Ignore Error
        }
      }
    };

    const productsItems = computed(() => store.value?.data.products?.items);

    const previewEnabled = computed(
      () => instance.value?.registeredComponents.has(ComponentName.preview) ?? false,
    );

    const preview = async (product: ProductItem) => {
      try {
        await instance.value?.execute(new PreviewCommand({ productId: product.productId }));
      } catch (error) {
        // Ignore Error
      }
    };

    const configurators = computed(() => store.value?.data.configurators?.items);

    const showConfigurator = async (configurator: ConfiguratorItem) => {
      try {
        await instance.value?.execute(new ConfiguratorCommand(configurator));
      } catch (error) {
        // Ignore Error
      }
    };

    const replacementsVisible = computed(
      () => store.value?.options.components?.common?.hideReplacements != true,
    );

    const showActionTemplate = (product: ProductItem) => {
      return (
        componentOptions.value.actionTemplate &&
        (componentOptions.value.showActionTemplateIfInactive || !product?.inactive)
      );
    };

    return {
      root,
      instance,
      componentName,
      componentOptions,
      isWebComponent,
      isReady,
      store,
      hideIfInitial,
      productsItems,
      previewEnabled,
      configurators,
      configuratorOptions,
      replacementsVisible,
      currentCIDName,
      showActionTemplate,
      showConfigurator,
      t,
      widerThan,
      attrValue,
      attrValues,
      details,
      additionalInfo,
      preview,
      emit,
    };
  },
});
